// Progressive loader for Insurance Admin Data (insurance/admin-data endpoint)
// Loads month-by-month when the selected range spans more than one month

import api from './adminData'
import mergeSummaryByDoctor from '@/core/utils/mergeSummaryByDoctor'
import progressiveAdminDataLoader from '@/core/services/api/admin/progressiveAdminDataLoader'
import { extractPaginatorTotals, loadGlobalPage } from '@/core/utils/progressivePagination'

class ProgressiveInsuranceAdminDataLoader {
  constructor() {
    this.isLoading = false
    this.currentProgress = 0
    this.totalMonths = 0
    this.onProgressCallback = null
    this.onCompleteCallback = null
    this.onErrorCallback = null
    this.abortRequested = false
  }

  requestAbort() {
    this.abortRequested = true
  }

  parseDateTimeString(dateTimeStr) {
    if (!dateTimeStr || typeof dateTimeStr !== 'string') return new Date(NaN)
    const [datePart, timePart = '00:00:00'] = dateTimeStr.split(' ')
    const [m, d, y] = datePart.split('-').map(Number)
    const [hh, mm, ss] = timePart.split(':').map(Number)
    return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0)
  }

  formatForFilter(dateObj, timeStr = '00:00:00') {
    const y = dateObj.getFullYear()
    const m = dateObj.getMonth() + 1
    const d = dateObj.getDate()
    return `${m}-${d}-${y} ${timeStr}`
  }

  calculateMonthsBetween(startDateTime, endDateTime) {
    const start = this.parseDateTimeString(startDateTime)
    const end = this.parseDateTimeString(endDateTime)
    const yearDiff = end.getFullYear() - start.getFullYear()
    const monthDiff = end.getMonth() - start.getMonth()
    return yearDiff * 12 + monthDiff + 1
  }

  generateMonthlyRanges(startDateTime, endDateTime) {
    const start = this.parseDateTimeString(startDateTime)
    const end = this.parseDateTimeString(endDateTime)
    const startTime = startDateTime.split(' ')[1] || '00:00:00'
    const endTime = endDateTime.split(' ')[1] || '23:59:59'
    const ranges = []
    let current = new Date(start.getFullYear(), start.getMonth(), 1)
    while (current <= end) {
      const isFirstMonth =
        current.getFullYear() === start.getFullYear() &&
        current.getMonth() === start.getMonth()
      const isLastMonth =
        current.getFullYear() === end.getFullYear() &&
        current.getMonth() === end.getMonth()
      const monthStart = new Date(current.getFullYear(), current.getMonth(), 1)
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0)
      const rangeStartDate = isFirstMonth ? start : monthStart
      const rangeEndDate = isLastMonth ? end : monthEnd
      const rangeStart = this.formatForFilter(
        rangeStartDate,
        isFirstMonth ? startTime : '00:00:00'
      )
      const rangeEnd = this.formatForFilter(
        rangeEndDate,
        isLastMonth ? endTime : '23:59:59'
      )
      ranges.push({
        start: rangeStart,
        end: rangeEnd,
        month: current.getMonth() + 1,
        year: current.getFullYear(),
      })
      current.setMonth(current.getMonth() + 1)
    }
    return ranges
  }

  async loadMonthData(dateRange, baseFilters, dataExcel = false) {
    const payload = {
      ...baseFilters,
      start_date: dateRange.start,
      end_date: dateRange.end,
      data_excel: dataExcel,
    }
    return api.getAdminData(payload, { throwOnError: true }).then(({ data }) => data)
  }

  /**
   * Carga mes a mes todos los datos (totales, promedios y tablas) y los une.
   * Sustituye la petición única con el rango completo, que el servidor corta en rangos largos.
   * @returns {Promise<object>} respuesta combinada con la misma forma que un solo mes
   */
  async loadProgressiveData(filters, dataExcel = false) {
    const { start_date, end_date } = filters || {}
    this.isLoading = true
    this.abortRequested = false
    const monthlyRanges = this.generateMonthlyRanges(start_date, end_date)
    this.totalMonths = monthlyRanges.length
    this.currentProgress = 0

    try {
      const monthlyDataArray = []
      for (let i = 0; i < monthlyRanges.length; i++) {
        if (this.abortRequested) throw new Error('ABORTED_BY_USER')
        const range = monthlyRanges[i]
        const monthData = await this.loadMonthData(range, filters, dataExcel)
        monthlyDataArray.push(monthData || {})
        this.currentProgress = i + 1
        if (this.onProgressCallback) {
          this.onProgressCallback({
            current: this.currentProgress,
            total: this.totalMonths,
            percentage: Math.round((this.currentProgress / this.totalMonths) * 100),
            currentMonth: range.month,
            currentYear: range.year,
          })
        }
        if (i < monthlyRanges.length - 1) await new Promise(r => setTimeout(r, 100))
      }

      this.lastFilters = { ...filters }
      this.lastBlocks = monthlyRanges.map((range, index) => ({
        range,
        totals: extractPaginatorTotals(monthlyDataArray[index]),
      }))
      return progressiveAdminDataLoader.combineMonthlyData(monthlyDataArray)
    } catch (error) {
      if (this.onErrorCallback) this.onErrorCallback(error)
      throw error
    } finally {
      this.isLoading = false
    }
  }

  /**
   * Página global de la tabla de consultas y/o de las campañas de la última carga progresiva.
   * @param {number} page
   * @param {{ rows?: boolean, campaigns?: boolean }} targets
   */
  async loadPage(page, targets) {
    if (!this.lastBlocks?.length) throw new Error('NO_PROGRESSIVE_DATA')
    return loadGlobalPage(
      this.lastBlocks,
      page,
      async (range, localPage) => {
        const response = await api.getAdminDataPaginated(
          localPage,
          { ...this.lastFilters, start_date: range.start, end_date: range.end, data_excel: false },
          { throwOnError: true }
        )
        return response?.data || {}
      },
      targets
    )
  }

  // public helper used by overlays
  combinePaginate(p1, p2) {
    if (!p1) return p2 || null
    if (!p2) return p1
    const d1 = Array.isArray(p1.data) ? p1.data : []
    const d2 = Array.isArray(p2.data) ? p2.data : []
    const mergedData = d1.concat(d2)
    const per_page = 5
    const total = (Number(p1.total || 0) || 0) + (Number(p2.total || 0) || 0)
    const path = p1.path || p2.path || ''
    const makeUrl = page => (path ? `${path}?page=${page}` : null)
    const last_page = per_page > 0 ? Math.ceil(total / per_page) : 1
    return {
      current_page: 1,
      data: mergedData.slice(0, per_page),
      first_page_url: makeUrl(1),
      from: total > 0 ? 1 : 0,
      last_page,
      last_page_url: makeUrl(last_page),
      links: [],
      next_page_url: null,
      path,
      per_page,
      prev_page_url: null,
      to: Math.min(total, per_page),
      total,
    }
  }

  async loadProgressiveOverlays(filters) {
    const { start_date, end_date } = filters || {}
    const monthsBetween = this.calculateMonthsBetween(start_date, end_date)

    if (monthsBetween <= 1) {
      const resp = await api.getAdminData(
        { ...filters, data_excel: false },
        { throwOnError: true }
      )
      const data = resp?.data || {}
      const overlay = {}
      if (data.ROWS_PAGINATE) overlay.ROWS_PAGINATE = data.ROWS_PAGINATE
      if (data.CAMPAIGN_CUPONS) overlay.CAMPAIGN_CUPONS = data.CAMPAIGN_CUPONS
      if (data.REFERENCES) overlay.REFERENCES = data.REFERENCES
      return overlay
    }

    this.isLoading = true
    this.abortRequested = false
    const monthlyRanges = this.generateMonthlyRanges(start_date, end_date)
    this.totalMonths = monthlyRanges.length
    this.currentProgress = 0

    try {
      const monthlyDataArray = []
      for (let i = 0; i < monthlyRanges.length; i++) {
        if (this.abortRequested) throw new Error('ABORTED_BY_USER')
        const range = monthlyRanges[i]
        const monthData = await this.loadMonthData(range, filters)
        monthlyDataArray.push(monthData)
        this.currentProgress = i + 1
        if (this.onProgressCallback) {
          this.onProgressCallback({
            current: this.currentProgress,
            total: this.totalMonths,
            percentage: Math.round((this.currentProgress / this.totalMonths) * 100),
            currentMonth: range.month,
            currentYear: range.year,
          })
        }
        if (i < monthlyRanges.length - 1) await new Promise(r => setTimeout(r, 100))
      }

      const overlay = {}

      // ROWS_PAGINATE
      try {
        const rowsList = monthlyDataArray.map(m => m?.ROWS_PAGINATE).filter(Boolean)
        if (rowsList.length)
          overlay.ROWS_PAGINATE = rowsList.reduce(
            (acc, cur) => this.combinePaginate(acc, cur),
            null
          )
      } catch (_) {}

      // CAMPAIGN_CUPONS
      try {
        const allKeys = new Set()
        monthlyDataArray.forEach(m => {
          const cc = m?.CAMPAIGN_CUPONS
          if (cc && typeof cc === 'object') Object.keys(cc).forEach(k => allKeys.add(k))
        })
        if (allKeys.size) {
          const result = {}
          allKeys.forEach(key => {
            let bucket = null
            monthlyDataArray.forEach(m => {
              const entry = m?.CAMPAIGN_CUPONS?.[key]
              if (entry) {
                if (!bucket) bucket = JSON.parse(JSON.stringify(entry))
                else {
                  const td1 = Number(bucket?.TOTAL_DISCOUNT || 0)
                  const td2 = Number(entry?.TOTAL_DISCOUNT || 0)
                  bucket.TOTAL_DISCOUNT = td1 + td2
                  bucket.CUPONS_PAGINATE = this.combinePaginate(
                    bucket.CUPONS_PAGINATE,
                    entry.CUPONS_PAGINATE
                  )
                }
              }
            })
            const campaignValue = Number(key)
            if (bucket && bucket.CUPONS_PAGINATE && campaignValue > 0) {
              const sumDiscount = Number(bucket.TOTAL_DISCOUNT || 0)
              const derivedTotal = Math.round(sumDiscount / campaignValue)
              bucket.CUPONS_PAGINATE.total = derivedTotal
            }
            result[key] = bucket
          })
          overlay.CAMPAIGN_CUPONS = result
        }
      } catch (_) {}

      // REFERENCES
      try {
        const aggregateRefs = selector => {
          // Recolectar dinámicamente todas las labels que aparecen en los datos
          const allLabelsSet = new Set()
          monthlyDataArray.forEach(m => {
            const arr = m?.REFERENCES?.[selector]
            if (Array.isArray(arr)) {
              arr.forEach(it => {
                const text = String(it?.references || '')
                const idx = text.indexOf(':')
                if (idx !== -1) {
                  const label = text.slice(0, idx).trim()
                  if (label) {
                    allLabelsSet.add(label)
                  }
                }
              })
            }
          })

          // Convertir a array ordenado para mantener consistencia
          const allLabels = Array.from(allLabelsSet).sort()

          // Inicializar el Map con todas las labels encontradas
          const sums = new Map(allLabels.map(l => [l, 0]))

          // Procesar todos los datos y sumar valores
          monthlyDataArray.forEach(m => {
            const arr = m?.REFERENCES?.[selector]
            if (Array.isArray(arr)) {
              arr.forEach(it => {
                const text = String(it?.references || '')
                const idx = text.indexOf(':')
                if (idx !== -1) {
                  const label = text.slice(0, idx).trim()
                  const num = Number(text.slice(idx + 1).trim() || 0)
                  if (!isNaN(num) && label) {
                    // Si la label no existe en el Map, agregarla
                    if (!sums.has(label)) {
                      sums.set(label, 0)
                      allLabels.push(label)
                      allLabels.sort()
                    }
                    sums.set(label, (sums.get(label) || 0) + num)
                  }
                }
              })
            }
          })

          // Retornar todas las labels encontradas (ordenadas) con sus valores
          return allLabels.map(l => ({ references: `${l}: ${sums.get(l) || 0}` }))
        }
        overlay.REFERENCES = { NC: aggregateRefs('NC'), NP: aggregateRefs('NP') }
      } catch (_) {}

      try {
        const byDoctor = mergeSummaryByDoctor(monthlyDataArray)
        if (byDoctor.length) overlay.SUMMARY_BY_DOCTOR = byDoctor
      } catch (_) {}

      this.isLoading = false
      return overlay
    } catch (error) {
      this.isLoading = false
      if (this.onErrorCallback) this.onErrorCallback(error)
      throw error
    }
  }

  onProgress(callback) {
    this.onProgressCallback = callback
  }
  onComplete(callback) {
    this.onCompleteCallback = callback
  }
  onError(callback) {
    this.onErrorCallback = callback
  }
}

const progressiveInsuranceAdminDataLoader = new ProgressiveInsuranceAdminDataLoader()
export default progressiveInsuranceAdminDataLoader
